import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { 
  Pin, 
  ChevronRight,
  CheckCircle2,
  Clock,
  AlertCircle,
  Lock,
  ExternalLink,
  Layers
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PinnedTaskBundles = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [pinnedBundles, setPinnedBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedBundle, setSelectedBundle] = useState(null);

  const fetchPinnedBundles = useCallback(async () => {
    if (!token) {
      console.log('PinnedTaskBundles: No token available');
      setLoading(false);
      return;
    }
    console.log('PinnedTaskBundles: Fetching pinned bundles...');
    try {
      const response = await fetch(`${API}/bundles/pinned`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('PinnedTaskBundles: Response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('PinnedTaskBundles: Received data:', data);
        setPinnedBundles(data);
      }
    } catch (error) {
      console.error('PinnedTaskBundles: Error fetching pinned bundles:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchPinnedBundles();
    }
  }, [token, fetchPinnedBundles]);

  const openDetailModal = (bundle) => {
    setSelectedBundle(bundle);
    setShowDetailModal(true);
  };

  const navigateToTask = (taskId) => {
    setShowDetailModal(false);
    navigate(`/tasks/${taskId}`);
  };

  const navigateToBundles = () => {
    navigate('/bundles');
  };

  const getStatusIcon = (status, isBlocked) => {
    if (isBlocked) {
      return <Lock className="w-4 h-4 text-orange-500" />;
    }
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'in progress':
        return <Clock className="w-4 h-4 text-blue-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-muted-foreground" />;
    }
  };

  // Always render for debugging - show loading or empty state
  if (loading) {
    return (
      <Card className="col-span-full" data-testid="pinned-task-bundles-loading">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Pin className="w-5 h-5 text-primary" />
            Pinned Task Bundles
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4 text-muted-foreground">Loading pinned bundles...</div>
        </CardContent>
      </Card>
    );
  }

  if (pinnedBundles.length === 0) {
    return null; // Hide when no pinned bundles
  }

  return (
    <>
      <Card className="col-span-full" data-testid="pinned-task-bundles">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Pin className="w-5 h-5 text-primary" />
            Pinned Task Bundles
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={navigateToBundles}
            className="text-muted-foreground hover:text-foreground"
            data-testid="view-all-bundles"
          >
            View All
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-4 text-muted-foreground">Loading...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {pinnedBundles.map((bundle) => (
                <div
                  key={bundle.id}
                  className="p-4 rounded-xl border bg-card hover:bg-muted/50 hover:shadow-md transition-all duration-200"
                  data-testid={`pinned-bundle-${bundle.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-medium text-sm truncate flex-1">{bundle.name}</h4>
                    <Pin className="w-4 h-4 text-primary fill-current flex-shrink-0 ml-2" />
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="flex justify-between text-xs text-muted-foreground mb-1">
                      <span>{bundle.completed_tasks}/{bundle.total_tasks} tasks</span>
                      <span>{bundle.progress_percent}%</span>
                    </div>
                    <Progress value={bundle.progress_percent} className="h-1.5" />
                  </div>
                  
                  {/* Task Preview (max 3, then "More...") */}
                  <div className="space-y-1 mb-3">
                    {bundle.tasks.slice(0, 3).map((task) => (
                      <div 
                        key={task.task_id}
                        className="flex items-center gap-2 text-xs text-muted-foreground"
                      >
                        {getStatusIcon(task.task_status, task.is_blocked)}
                        <span className={`truncate ${task.task_status === 'completed' ? 'line-through' : ''}`}>
                          {task.task_name}
                        </span>
                      </div>
                    ))}
                    {bundle.tasks.length > 3 && (
                      <div className="text-xs text-muted-foreground/70 pl-6">
                        More... ({bundle.tasks.length - 3})
                      </div>
                    )}
                    {bundle.total_tasks === bundle.completed_tasks && bundle.total_tasks > 0 && (
                      <div className="flex items-center gap-1 text-xs text-green-500 font-medium">
                        <CheckCircle2 className="w-3 h-3" />
                        Complete!
                      </div>
                    )}
                  </div>
                  
                  {/* View Button */}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full"
                    onClick={() => openDetailModal(bundle)}
                    data-testid={`view-bundle-${bundle.id}`}
                  >
                    View
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bundle Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          {selectedBundle && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Layers className="w-5 h-5" />
                  {selectedBundle.name}
                </DialogTitle>
              </DialogHeader>
              
              <div className="flex-1 overflow-y-auto space-y-4 py-4">
                {/* Bundle Info */}
                {selectedBundle.description && (
                  <p className="text-sm text-muted-foreground">{selectedBundle.description}</p>
                )}
                <p className="text-sm">
                  <span className="text-muted-foreground">Assigned to: </span>
                  {selectedBundle.assigned_to_name || 'Unassigned'}
                </p>
                
                {/* Overall Progress */}
                <div className="p-4 rounded-lg bg-muted/50">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="font-medium">Overall Progress</span>
                    <span className="text-muted-foreground">
                      {selectedBundle.completed_tasks}/{selectedBundle.total_tasks} tasks completed
                    </span>
                  </div>
                  <Progress value={selectedBundle.progress_percent} className="h-3" />
                  <div className="text-right text-sm text-muted-foreground mt-1">
                    {selectedBundle.progress_percent}%
                  </div>
                </div>
                
                {/* Tasks List */}
                <div>
                  <h4 className="font-medium mb-3">Tasks</h4>
                  <div className="space-y-2">
                    {selectedBundle.tasks.map((task, idx) => (
                      <div 
                        key={task.task_id}
                        className={`p-3 rounded-lg border transition-all ${
                          task.is_blocked ? 'bg-orange-500/5 border-orange-500/20' : 'bg-card hover:bg-muted/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <span className="text-muted-foreground text-sm w-5">{idx + 1}.</span>
                            {getStatusIcon(task.task_status, task.is_blocked)}
                            <span className={`truncate ${task.task_status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                              {task.task_name}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {task.is_blocked && (
                              <Badge variant="outline" className="text-xs bg-orange-500/10 text-orange-600">
                                Blocked
                              </Badge>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigateToTask(task.task_id)}
                              className="h-8 px-2"
                              data-testid={`go-to-task-${task.task_id}`}
                            >
                              Go
                              <ExternalLink className="w-3 h-3 ml-1" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Subtask Progress */}
                        {task.subtask_total > 0 && (
                          <div className="flex items-center gap-2 mt-2 ml-8 text-xs text-muted-foreground">
                            <span>Sub-tasks: {task.subtask_completed}/{task.subtask_total}</span>
                            <div className="flex-1 max-w-32 h-1.5 bg-secondary/50 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-primary transition-all duration-500"
                                style={{ width: `${task.subtask_progress}%` }}
                              />
                            </div>
                            <span>{task.subtask_progress}%</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <DialogFooter className="gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowDetailModal(false);
                    navigate('/bundles');
                  }}
                >
                  Edit Bundle
                </Button>
                <Button onClick={() => setShowDetailModal(false)}>
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PinnedTaskBundles;
